import { ReactNode } from 'react';
import clsx from 'clsx';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    label: string;
    isPositive?: boolean;
  };
  color?: 'orange' | 'purple' | 'cyan' | 'green' | 'red' | 'blue' | 'yellow';
  glow?: boolean;
}

export function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  color = 'orange',
  glow = true,
}: StatCardProps) {
  const colorClasses = {
    orange: {
      bg: 'bg-orange-500/20',
      text: 'text-orange-500',
      border: 'border-orange-500/30',
      glow: 'shadow-orange-500/30',
    },
    purple: {
      bg: 'bg-purple-500/20',
      text: 'text-purple-500',
      border: 'border-purple-500/30',
      glow: 'shadow-purple-500/30',
    },
    cyan: {
      bg: 'bg-cyan-500/20',
      text: 'text-cyan-500',
      border: 'border-cyan-500/30',
      glow: 'shadow-cyan-500/30',
    },
    green: {
      bg: 'bg-green-500/20',
      text: 'text-green-500',
      border: 'border-green-500/30',
      glow: 'shadow-green-500/30',
    },
    red: {
      bg: 'bg-red-500/20',
      text: 'text-red-500',
      border: 'border-red-500/30',
      glow: 'shadow-red-500/30',
    },
    blue: {
      bg: 'bg-blue-500/20',
      text: 'text-blue-500',
      border: 'border-blue-500/30',
      glow: 'shadow-blue-500/30',
    },
    yellow: {
      bg: 'bg-yellow-500/20',
      text: 'text-yellow-500',
      border: 'border-yellow-500/30',
      glow: 'shadow-yellow-500/30',
    },
  };

  const colors = colorClasses[color];

  return (
    <div
      className={clsx(
        'relative rounded-xl p-6 transition-all duration-300',
        'bg-slate-800 border',
        colors.border,
        'hover:-translate-y-1',
        {
          [`hover:shadow-lg ${colors.glow}`]: glow,
        }
      )}
    >
      {/* Background glow effect */}
      {glow && (
        <div
          className={clsx(
            'absolute inset-0 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300',
            colors.bg
          )}
        />
      )}

      {/* Content */}
      <div className="relative z-10 flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-400 mb-1">{title}</p>
          <p className="text-3xl font-bold text-white mb-2">{value}</p>

          {trend && (
            <div className="flex items-center gap-1 text-sm">
              <span
                className={clsx(
                  'font-medium',
                  trend.isPositive ? 'text-green-400' : 'text-red-400'
                )}
              >
                {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
              </span>
              <span className="text-slate-500">{trend.label}</span>
            </div>
          )}
        </div>

        {/* Icon with background */}
        <div
          className={clsx(
            'w-12 h-12 rounded-lg flex items-center justify-center',
            colors.bg
          )}
        >
          <Icon className={clsx('w-6 h-6', colors.text)} />
        </div>
      </div>
    </div>
  );
}

/**
 * Animated loading skeleton for stat cards
 */
export function StatCardSkeleton() {
  return (
    <div className="rounded-xl p-6 bg-slate-800 border border-slate-700">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="h-4 w-24 bg-slate-700 rounded skeleton mb-2" />
          <div className="h-8 w-32 bg-slate-700 rounded skeleton mb-2" />
          <div className="h-4 w-28 bg-slate-700 rounded skeleton" />
        </div>
        <div className="w-12 h-12 bg-slate-700 rounded-lg skeleton" />
      </div>
    </div>
  );
}

/**
 * Grid container for stat cards
 */
interface StatGridProps {
  children: ReactNode;
  columns?: 1 | 2 | 3 | 4;
}

export function StatGrid({ children, columns = 4 }: StatGridProps) {
  const columnClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={clsx('grid gap-6 stagger-children', columnClasses[columns])}>
      {children}
    </div>
  );
}
